import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpParamsRecord, ListSortQuery, applyListSortQuery } from 'app/core/utils/list-sort.util';
import {
  ApiResponse,
  SalesChargeLineResponse,
  SalesChargeLineUpsertBody,
  SalesDocumentCancelRequest,
  SalesInvoiceListItem,
  SalesInvoiceResponse,
  SalesInvoiceUpsertRequest,
} from 'app/modules/sales/sales.models';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SalesInvoicesService {

  constructor(private _http: HttpClient) {}

  getSalesInvoices(
    page?: number,
    size?: number,
    filters?: {
      keyword?: string;
      customerId?: string;
      isDirect?: boolean;
      salesOrderId?: string | number;
      deliveryOrderId?: string | number;
      fromDate?: string;
      toDate?: string;
    } & ListSortQuery
  ): Observable<ApiResponse<SalesInvoiceListItem[]> | SalesInvoiceListItem[]> {
    const params: HttpParamsRecord = {};
    if (page !== undefined) params.pageNumber = page;
    if (size !== undefined) params.pageSize = size;
    if (filters?.keyword) params.keyword = filters.keyword;
    if (filters?.customerId) params.customerId = filters.customerId;
    if (filters?.isDirect !== undefined && filters?.isDirect !== null) params.isDirect = filters.isDirect;
    if (filters?.salesOrderId != null) params.salesOrderId = filters.salesOrderId;
    if (filters?.deliveryOrderId != null) params.deliveryOrderId = filters.deliveryOrderId;
    if (filters?.fromDate) params.fromDate = filters.fromDate;
    if (filters?.toDate) params.toDate = filters.toDate;
    applyListSortQuery(params, filters);
    return this._http.get<ApiResponse<SalesInvoiceListItem[]> | SalesInvoiceListItem[]>(
      `${environment.apiUrl}sales-invoices`,
      { params }
    );
  }

  getSalesInvoice(id: string | number): Observable<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse> {
    return this._http.get<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse>(
      `${environment.apiUrl}sales-invoices/${id}`
    );
  }

  createSalesInvoice(
    body: SalesInvoiceUpsertRequest
  ): Observable<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse> {
    return this._http.post<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse>(
      `${environment.apiUrl}sales-invoices`,
      body
    );
  }

  createSalesInvoiceFromSalesOrder(
    salesOrderId: string | number
  ): Observable<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse> {
    return this._http.post<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse>(
      `${environment.apiUrl}sales-invoices/from-sales-order/${salesOrderId}`,
      {}
    );
  }

  updateSalesInvoice(
    id: string | number,
    body: SalesInvoiceUpsertRequest
  ): Observable<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse> {
    return this._http.put<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse>(
      `${environment.apiUrl}sales-invoices/${id}`,
      body
    );
  }

  postSalesInvoice(id: string | number): Observable<unknown> {
    return this._http.post(`${environment.apiUrl}sales-invoices/${id}/post`, {});
  }

  createSalesInvoiceAndPost(
    body: SalesInvoiceUpsertRequest
  ): Observable<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse> {
    return this._http.post<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse>(
      `${environment.apiUrl}sales-invoices/create-post`,
      body
    );
  }

  updateSalesInvoiceAndPost(
    id: string | number,
    body: SalesInvoiceUpsertRequest
  ): Observable<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse> {
    return this._http.put<ApiResponse<SalesInvoiceResponse> | SalesInvoiceResponse>(
      `${environment.apiUrl}sales-invoices/${id}/create-post`,
      body
    );
  }

  cancelSalesInvoice(id: string | number, body: SalesDocumentCancelRequest = {}): Observable<unknown> {
    return this._http.post(`${environment.apiUrl}sales-invoices/${id}/cancel`, body);
  }

  deleteSalesInvoice(id: string | number): Observable<unknown> {
    return this._http.delete(`${environment.apiUrl}sales-invoices/${id}`);
  }

  getCharges(salesInvoiceId: string | number): Observable<SalesChargeLineResponse[]> {
    return this._http.get<SalesChargeLineResponse[]>(
      `${environment.apiUrl}sales-invoices/${salesInvoiceId}/charges`
    );
  }

  addCharge(
    salesInvoiceId: string | number,
    body: SalesChargeLineUpsertBody
  ): Observable<SalesChargeLineResponse> {
    return this._http.post<SalesChargeLineResponse>(
      `${environment.apiUrl}sales-invoices/${salesInvoiceId}/charges`,
      body
    );
  }

  updateCharge(
    salesInvoiceId: string | number,
    chargeLineId: string | number,
    body: SalesChargeLineUpsertBody
  ): Observable<SalesChargeLineResponse> {
    return this._http.put<SalesChargeLineResponse>(
      `${environment.apiUrl}sales-invoices/${salesInvoiceId}/charges/${chargeLineId}`,
      body
    );
  }

  deleteCharge(salesInvoiceId: string | number, chargeLineId: string | number): Observable<unknown> {
    return this._http.delete(
      `${environment.apiUrl}sales-invoices/${salesInvoiceId}/charges/${chargeLineId}`
    );
  }
}
